"use client"
import {DATA_COLUMN_TYPES} from "@/app/components/auctor/result/ResultColumn"
import ResultButton from "@/app/components/auctor/result/ResultButton"
import {IAppliedFilter, IBannerConfig, IColumnConfig, ITemplateConfig} from "@/app/models/auctor"
import {evaluateExpressionWithFiltersAndRowData, getClientQueryParams} from "@/app/utils/expression-utils"
import dynamic from "next/dynamic"
import Image from "next/image"
import {Suspense, useEffect, useState} from "react"
import Skeleton from "react-loading-skeleton"
import "react-loading-skeleton/dist/skeleton.css"

const ResultBanner = dynamic(() => import("@/app/components/auctor/result/ResultBanner"))
const AppIcon = dynamic(() => import("@/app/components/atomic/AppIcon"))

function BannerSkeleton() {
    return <div className="w-full h-8 bg-gray-200 animate-pulse rounded-t-2xl"/>
}

interface ResultRowProps {
    resultRow: any
    templateConfig: ITemplateConfig
    appliedFilters: IAppliedFilter[]
    isPreview?: boolean
    isFeatured?: boolean
}

export default function ResultRow({
                                      resultRow,
                                      templateConfig,
                                      appliedFilters,
                                      isFeatured = false,
                                      isPreview = false,
                                  }: ResultRowProps) {
    const [enabledBanner, setEnabledBanner] = useState<IBannerConfig | null>(null)

    const queryParams = getClientQueryParams()

    useEffect(() => {
        if (!templateConfig) {
            return
        }

        let bannerToDisplay = structuredClone(getEnabledBannerConfig(resultRow, templateConfig))

        if (isFeatured === true) {
            const featuredText = "Notre recommandation"
            const featuredIcon = "fa-medal"
            const featuredColor = "#5cb85c"

            if (!bannerToDisplay) {
                bannerToDisplay = {} as IBannerConfig
            }

            if (bannerToDisplay.content) {
                bannerToDisplay.content = `${featuredText} - ${bannerToDisplay.content}`
            } else {
                bannerToDisplay.content = featuredText
            }

            bannerToDisplay.fa_icon = featuredIcon
            bannerToDisplay.color = featuredColor
        }

        setEnabledBanner(bannerToDisplay as IBannerConfig)
    }, [resultRow, templateConfig, isFeatured])

    const getEnabledBannerConfig = (resultRow: any, templateConfig: any): IBannerConfig | null => {
        let enabledBannerConfig = null
        templateConfig.banner_configs.forEach((bannerConfigFromTemplate: any) => {
            if (!bannerConfigFromTemplate.is_custom_banner && resultRow[bannerConfigFromTemplate.banner_column_name]) {
                enabledBannerConfig = bannerConfigFromTemplate
                enabledBannerConfig.content = resultRow[bannerConfigFromTemplate.banner_column_name]
            }

            if (bannerConfigFromTemplate.is_custom_banner && bannerConfigFromTemplate.enable_condition) {
                try {
                    let expression = bannerConfigFromTemplate.enable_condition
                    if (!expression.includes("return")) {
                        expression = `return ${expression}`
                    }

                    const expressionFunction = new Function("data", expression)
                    const isEnabled = expressionFunction(resultRow)

                    if (isEnabled) {
                        enabledBannerConfig = bannerConfigFromTemplate
                    }
                } catch (error) {
                    console.error(`Error evaluating condition for banner`, error)
                    return null
                }
            }
        })

        return enabledBannerConfig
    }

    const evalExpr = (expression: string | null | undefined) =>
        expression
            ? evaluateExpressionWithFiltersAndRowData(
                  expression,
                  resultRow,
                  appliedFilters,
                  undefined,
                  queryParams
              ) ?? ""
            : ""

    const columnConfigs = templateConfig?.column_configs ?? []
    const logoColumn = columnConfigs.find((c: IColumnConfig) => c.column_type === DATA_COLUMN_TYPES.IMAGE_URL)
    const expressionColumn = columnConfigs.find((c: IColumnConfig) => c.column_type === DATA_COLUMN_TYPES.EXPRESSION)
    const htmlColumn = columnConfigs.find((c: IColumnConfig) => c.column_type === DATA_COLUMN_TYPES.HTML)
    const buttonColumn = columnConfigs.find((c: IColumnConfig) => c.column_type === DATA_COLUMN_TYPES.BUTTON)

    const ratingColumnName = templateConfig?.rating_column_name
    const ratingValue = ratingColumnName ? resultRow[ratingColumnName] : null
    const hasRating = ratingValue !== undefined && ratingValue !== null && Number.isFinite(parseFloat(ratingValue))

    const col1Span = logoColumn?.col_span ?? 2
    const col2Span = expressionColumn?.col_span ?? 4
    const col3Span = Math.max(1, 12 - col1Span - col2Span)

    return (
        <>
            <tr className="relative w-full md:h-50 overflow-hidden rounded-2xl">
                <td
                    className={`md:hidden bg-white rounded-2xl p-0 ${enabledBanner ? "pt-14 md:pt-10" : ""}`}
                    colSpan={3}
                >
                    <div className="flex flex-col">
                        <div className="flex items-center justify-between px-4 pt-4 gap-4">
                            {logoColumn?.data_column_name && resultRow[logoColumn.data_column_name] && (
                                <div className="relative w-32 h-16">
                                    <Image
                                        src={resultRow[logoColumn.data_column_name]}
                                        alt={logoColumn.label ?? "logo assureur"}
                                        fill
                                        className="object-contain object-left"
                                        sizes="40vw"
                                    />
                                </div>
                            )}

                            {hasRating && (
                                <div className="flex items-center bg-pastel-yellow rounded-2xl p-3 text-pale font-semibold">
                                    <AppIcon className="text-pastel-orange" type="solid" iconClassName="fa-star"/>
                                    <span className="bold title-look">{ratingValue}</span>
                                    <span className="text-rm-gray">/5</span>
                                </div>
                            )}
                        </div>

                        {logoColumn?.bottom_additional_expression && (
                            <div
                                className="px-4 pt-2"
                                dangerouslySetInnerHTML={{__html: evalExpr(logoColumn.bottom_additional_expression)}}
                            />
                        )}

                        {templateConfig?.price_expression && (
                            <div
                                className="px-4 pt-2 text-center title-look"
                                dangerouslySetInnerHTML={{__html: evalExpr(templateConfig.price_expression)}}
                            />
                        )}

                        <hr className="border-t border-rm-gray-light mx-12 my-4"/>

                        {expressionColumn && (
                            <div className="px-4 pb-2 text-sm">
                                {expressionColumn.top_additional_expression && (
                                    <div
                                        dangerouslySetInnerHTML={{__html: evalExpr(expressionColumn.top_additional_expression)}}
                                    />
                                )}
                                {expressionColumn.expression && (
                                    <div dangerouslySetInnerHTML={{__html: evalExpr(expressionColumn.expression)}}/>
                                )}
                                {expressionColumn.bottom_additional_expression && (
                                    <div
                                        dangerouslySetInnerHTML={{__html: evalExpr(expressionColumn.bottom_additional_expression)}}
                                    />
                                )}
                            </div>
                        )}

                        {htmlColumn && (
                            <div className="px-4 py-2 text-sm bg-rm-gray-light/50">
                                {htmlColumn.top_additional_expression && (
                                    <div
                                        dangerouslySetInnerHTML={{__html: evalExpr(htmlColumn.top_additional_expression)}}
                                    />
                                )}

                                {htmlColumn.data_column_name && (
                                    <div
                                        className="self-start font-light leading-normal"
                                        dangerouslySetInnerHTML={{__html: resultRow[htmlColumn.data_column_name] ?? ""}}
                                    />
                                )}

                                {htmlColumn.bottom_additional_expression && (
                                    <div
                                        dangerouslySetInnerHTML={{__html: evalExpr(htmlColumn.bottom_additional_expression)}}
                                    />
                                )}
                            </div>
                        )}

                        {buttonColumn && (
                            <div className="flex flex-col items-center gap-2 p-4">
                                {buttonColumn.top_additional_expression && (
                                    <div
                                        className="w-full"
                                        dangerouslySetInnerHTML={{__html: evalExpr(buttonColumn.top_additional_expression)}}
                                    />
                                )}
                                <ResultButton
                                    column={buttonColumn}
                                    resultRow={resultRow}
                                    appliedFilters={appliedFilters}
                                    queryParams={queryParams}
                                />
                                {buttonColumn.bottom_additional_expression && (
                                    <div
                                        className="w-full"
                                        dangerouslySetInnerHTML={{__html: evalExpr(buttonColumn.bottom_additional_expression)}}
                                    />
                                )}
                            </div>
                        )}
                    </div>
                </td>

                {/* Desktop col 1: logo + brand */}
                <td
                    className={`hidden md:table-cell bg-white align-top h-px rounded-l-2xl w-${col1Span}/12
                                ${enabledBanner ? "pt-10" : ""}`}
                >
                    {logoColumn && (
                        <div className="flex flex-col items-center gap-2 p-4 h-full justify-center">
                            {logoColumn.data_column_name && resultRow[logoColumn.data_column_name] && (
                                <div className="relative w-32 h-20">
                                    <Image
                                        src={resultRow[logoColumn.data_column_name]}
                                        alt={logoColumn.label ?? "logo assureur"}
                                        fill
                                        className="object-contain"
                                        sizes="(max-width: 768px) 40vw, 20vw"
                                    />
                                </div>
                            )}
                            {logoColumn.bottom_additional_expression && (
                                <div
                                    className="w-full"
                                    dangerouslySetInnerHTML={{__html: evalExpr(logoColumn.bottom_additional_expression)}}
                                />
                            )}
                        </div>
                    )}
                </td>

                {/* Desktop col 2: expression */}
                <td
                    className={`hidden md:table-cell bg-white h-px align-top w-${col2Span}/12
                                ${enabledBanner ? "pt-10" : ""}`}
                >
                    {expressionColumn && (
                        <div className="flex flex-col p-4 text-sm h-full justify-center">
                            {expressionColumn.top_additional_expression && (
                                <div dangerouslySetInnerHTML={{__html: evalExpr(expressionColumn.top_additional_expression)}}/>
                            )}
                            {expressionColumn.expression && (
                                <div dangerouslySetInnerHTML={{__html: evalExpr(expressionColumn.expression)}}/>
                            )}
                            {expressionColumn.bottom_additional_expression && (
                                <div dangerouslySetInnerHTML={{__html: evalExpr(expressionColumn.bottom_additional_expression)}}/>
                            )}
                        </div>
                    )}
                </td>

                {/* Desktop col 3: price + rating (top), html (middle), button (bottom). Left border = separator between col 2 and col 3. h-px is the "magic 1px" trick so inner h-full resolves to row height. */}
                <td
                    className={`hidden md:table-cell bg-white align-top rounded-r-2xl h-px w-${col3Span}/12 pt-10`}
                >
                    <div className="flex h-full">
                        <div className="w-px bg-rm-gray-light my-6"/>

                        <div className="flex flex-col justify-between p-4 flex-1">
                            <div className="flex items-start justify-between gap-3">
                                {templateConfig?.price_expression && (
                                    <div
                                        className="title-look"
                                        dangerouslySetInnerHTML={{__html: evalExpr(templateConfig.price_expression)}}
                                    />
                                )}
                                {hasRating && (
                                    <div className="flex items-center bg-pastel-yellow rounded-2xl py-1 px-2 text-pale font-semibold shrink-0">
                                        <AppIcon className="text-pastel-orange" type="solid" iconClassName="fa-star"/>
                                        <span className="bold title-look">{ratingValue}</span>
                                        <span className="text-rm-gray">/5</span>
                                    </div>
                                )}
                            </div>

                            {htmlColumn && (
                                <div className="text-sm bg-pastel-green/10 px-4 py-2 my-2 rounded-2xl">
                                    {htmlColumn.top_additional_expression && (
                                        <div dangerouslySetInnerHTML={{__html: evalExpr(htmlColumn.top_additional_expression)}}/>
                                    )}
                                    {htmlColumn.data_column_name && (
                                        <div
                                            className="font-light leading-normal"
                                            dangerouslySetInnerHTML={{__html: resultRow[htmlColumn.data_column_name] ?? ""}}
                                        />
                                    )}
                                    {htmlColumn.bottom_additional_expression && (
                                        <div dangerouslySetInnerHTML={{__html: evalExpr(htmlColumn.bottom_additional_expression)}}/>
                                    )}
                                </div>
                            )}

                            {buttonColumn && (
                                <div className="flex flex-col items-center gap-2">
                                    {buttonColumn.top_additional_expression && (
                                        <div className="w-full" dangerouslySetInnerHTML={{__html: evalExpr(buttonColumn.top_additional_expression)}}/>
                                    )}
                                    <ResultButton
                                        column={buttonColumn}
                                        resultRow={resultRow}
                                        appliedFilters={appliedFilters}
                                        queryParams={queryParams}
                                    />
                                    {buttonColumn.bottom_additional_expression && (
                                        <div className="w-full" dangerouslySetInnerHTML={{__html: evalExpr(buttonColumn.bottom_additional_expression)}}/>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </td>

                <td
                    className="absolute left-0 rounded-t-2xl md:rounded-tr-none md:rounded-br-2xl w-full md:w-fit"
                    style={{backgroundColor: enabledBanner?.color}}
                >
                    {enabledBanner && (
                        <Suspense fallback={<BannerSkeleton/>}>
                            <ResultBanner banner={enabledBanner} resultRow={resultRow}/>
                        </Suspense>
                    )}
                </td>
            </tr>
        </>
    )
}

export function ResultRowSkeleton({withBanner = false}) {
    return (
        <div className={"relative w-full"}>
            {withBanner && <BannerSkeleton/>}
            <div
                className={`relative grid bg-blue-100 w-full rounded-xl z-10 border border-blue-300
                md:h-50 md:grid-cols-4 overflow-hidden ${withBanner && "mt-9"}`}
            >
                <Skeleton
                    circle
                    inline
                    height={100}
                    width={100}
                    containerClassName="flex-col items-center justify-center p-4 hidden md:flex"
                />

                <Skeleton
                    inline
                    height={80}
                    width={160}
                    borderRadius={"1rem"}
                    containerClassName="flex flex-col items-center justify-center p-4 md:hidden"
                />

                <Skeleton
                    inline
                    width={100}
                    height={20}
                    containerClassName="h-40 md:h-full flex flex-col items-center justify-center p-2 bg-blue-100"
                />

                <Skeleton
                    count={4}
                    height={15}
                    containerClassName="flex-col items-center justify-center p-4 hidden md:flex"
                />

                <Skeleton
                    inline
                    height={32}
                    width={120}
                    containerClassName="flex flex-col items-center justify-center px-4 pb-2 pt-4 md:hidden"
                />

                <Skeleton
                    inline
                    height={50}
                    containerClassName="flex flex-col items-center justify-center px-4 pt-2 pb-4 w-full md:w-40 mx-auto"
                />
            </div>
        </div>
    )
}
